import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";
import { BookHeart, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";

export default function MemoryBook({ open, onClose, character }) {
  const [pages, setPages] = useState([]);
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !character) return;
    setLoading(true);
    // Opening the portal triggers background ad telemetry (slot: ad-slot-diary-interstitial).
    api.get(`/diary/${character.id}`).then(({ data }) => {
      setPages(data);
      setIdx(Math.max(0, data.length - 1));
    }).finally(() => setLoading(false));
  }, [open, character]);

  const page = pages[idx];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-transparent border-0 shadow-none max-w-xl p-0" data-testid="memory-book">
        <DialogTitle className="sr-only">The Memory Book</DialogTitle>
        <DialogDescription className="sr-only">Daily diary of your connection.</DialogDescription>

        {/* popunder ad injection target */}
        <div id="ad-slot-diary-interstitial" data-ad-slot="ad-slot-diary-interstitial" className="hidden" />

        <div className="relative rounded-2xl p-1.5"
          style={{ background: "linear-gradient(145deg,#6b4a2b,#3a2817)", boxShadow: "0 30px 60px rgba(0,0,0,0.6)" }}>
          <div className="rounded-xl overflow-hidden" style={{ background: "#f5ecd8", color: "#3a2817" }}>
            <div className="px-7 py-5 flex items-center gap-2 border-b" style={{ borderColor: "rgba(58,40,23,0.15)" }}>
              <BookHeart className="w-5 h-5" />
              <span className="font-heading font-bold tracking-wide">Our Memory Book</span>
            </div>
            <div className="px-8 py-10 min-h-[260px] [perspective:1200px]"
              style={{ fontFamily: "Georgia, serif", backgroundImage: "repeating-linear-gradient(transparent,transparent 31px,rgba(58,40,23,0.08) 32px)" }}>
              {loading ? (
                <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin" /></div>
              ) : page ? (
                <div key={idx} className="page-turn">
                  <p className="text-xs uppercase tracking-[0.25em] opacity-60 mb-1">Day {page.day} • {page.date}</p>
                  <p className="text-lg leading-relaxed italic">"{page.entry}"</p>
                  <p className="text-right mt-6 text-sm opacity-70">— {character?.name} 💌</p>
                </div>
              ) : (
                <p className="text-center opacity-60 py-10 italic">No entries yet. Keep chatting and your story will be written here each day.</p>
              )}
            </div>
            <div className="px-7 py-3 flex items-center justify-between border-t" style={{ borderColor: "rgba(58,40,23,0.15)" }}>
              <button data-testid="diary-prev" disabled={idx <= 0} onClick={() => setIdx((i) => Math.max(0, i - 1))}
                className="flex items-center gap-1 text-sm disabled:opacity-30"><ChevronLeft className="w-4 h-4" /> Prev</button>
              <span className="text-xs opacity-60">{pages.length ? `${idx + 1} / ${pages.length}` : "0 / 0"}</span>
              <button data-testid="diary-next" disabled={idx >= pages.length - 1} onClick={() => setIdx((i) => Math.min(pages.length - 1, i + 1))}
                className="flex items-center gap-1 text-sm disabled:opacity-30">Next <ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
